import { useEffect, useRef } from 'react';
import { useAuthStore } from '../lib/auth';

interface AuthProviderProps {
  children: React.ReactNode;
}

export default function AuthProvider({ children }: AuthProviderProps) {
  const { checkSession } = useAuthStore();
  const hasInitialized = useRef(false);
  const isCheckingSession = useRef(false);

  // Initialize auth state on mount (only once)
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
    checkSession();
    }
  }, [checkSession]);

  // Handle visibility change - only check session if it's been a while
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Only check session when tab becomes visible if it's been more than 5 minutes
      if (!document.hidden && !isCheckingSession.current) {
        const lastCheck = sessionStorage.getItem('lastSessionCheck');
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000;

        if (!lastCheck || (now - parseInt(lastCheck)) > fiveMinutes) {
          isCheckingSession.current = true;
          checkSession().finally(() => {
            isCheckingSession.current = false;
            sessionStorage.setItem('lastSessionCheck', now.toString());
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkSession]);

  return <>{children}</>;
}